import { makeAutoObservable, reaction } from "mobx";
import type RootStore from "./RootStore";
import { numericUserId } from "../auth/userId";

/**
 * "Turn on phone push" — the one-time notice in the bell.
 *
 * WHY IT EXISTS (plan/2026-09-19_phase-5-rails-that-reach-nobody.md §5.4).
 * `notification_preferences` treats absence as OFF, correctly, so the phone leg
 * of a ticket notification is off for everyone who has not found Settings →
 * Notifications on their own — and nothing in the app ever told anyone it was
 * there. The VAPID keys the operator owes would change nothing even once set.
 * The first time a ticket lands in someone's bell is the moment to say "you can
 * get these on your phone", so that is the only moment this appears.
 *
 * WHEN IT SHOWS: the bell holds at least one ticket notification addressed to
 * this person (not a broadcast), they have not dismissed it, and — when HQ
 * knows it, because the settings page was loaded this session — the push leg
 * does not already carry a ticket kind. When HQ does not know, it asks; an
 * invitation that is occasionally redundant costs one click, and the
 * alternative is to load a person's device and subscription state on every page
 * just to decide whether to show a sentence.
 *
 * WHERE THE DISMISSAL LIVES, AND WHY THERE. Per person, in this browser's
 * localStorage — the established home for a per-person UI convenience here
 * (`devops.scope`, the selected client, push/deviceOwner.ts). The server-side
 * alternative was weighed and refused: `user-preferences.preferences` is
 * REPLACED WHOLESALE by settingsSync.ts on every settings change (its PATCH
 * body is the settings store's own snapshot), so a key written there by anyone
 * else is silently erased the next time the person touches their theme — a
 * dismissal that comes back is worse than one that is per-browser. Every
 * storage access is guarded: a private window must still render, and there the
 * notice simply returns next session.
 *
 * Choosing "Turn on phone push" counts as a dismissal too: they have acted on
 * it, and the settings page is where the answer now lives.
 *
 * EXCEPT ON A DEPLOYMENT THAT CANNOT PUSH. With no VAPID keys the settings page
 * can only say "not configured", and a dismissal remembered then would keep the
 * notice away for good — including after the operator sets the keys, which is
 * exactly when it becomes true. So the notice is hidden while push is KNOWN to
 * be unconfigured, and "Turn on phone push" is remembered only once the
 * settings load has shown push IS configured; until then it holds for this
 * session only. The explicit close (×) is a "no thanks" and is remembered as
 * before.
 */

/** Notification `type`s in the ticket family (API push.policy.ts PUSH_TYPES). */
export const isTicketNotification = (n: {
  type?: unknown;
  entityType?: unknown;
}): boolean =>
  (typeof n.type === "string" && n.type.startsWith("ticket_")) ||
  n.entityType === "ticket";

export const dismissalKey = (userId: number): string =>
  `hq.pushNotice.dismissed.${userId}`;

const readDismissed = (userId: number): boolean => {
  try {
    return Boolean(globalThis.localStorage?.getItem(dismissalKey(userId)));
  } catch {
    return false;
  }
};

const writeDismissed = (userId: number): void => {
  try {
    globalThis.localStorage?.setItem(dismissalKey(userId), new Date().toISOString());
  } catch {
    // Storage is a convenience, never a requirement: the notice is gone for
    // this session regardless, and returns next time in a browser that forgets.
  }
};

interface BellNotification {
  type?: unknown;
  entityType?: unknown;
  userId?: number | null;
}

class PushNoticeStore {
  rootStore: RootStore;

  /** The person `dismissed` was read for; a different one re-reads it. */
  private dismissedFor: number | null = null;
  dismissed = false;
  /** "Turn on phone push" was chosen, but whether push can work is not known yet. */
  private answeredFor: number | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    // Re-read the dismissal whenever the person changes — including the first
    // sign-in, and a switch of account on a shared browser.
    reaction(
      () => this.currentUserId(),
      (id) => this.loadFor(id),
      { fireImmediately: true },
    );
    // Remember a "Turn on phone push" once the settings load says push can work.
    reaction(
      () => this.pushConfigured,
      (configured) => {
        const id = this.currentUserId();
        if (configured === true && id !== null && this.answeredFor === id) {
          writeDismissed(id);
          this.answeredFor = null;
        }
      },
    );
  }

  private currentUserId(): number | null {
    return numericUserId(
      (this.rootStore as unknown as {
        authStore?: { currentUser?: { id?: unknown } };
      }).authStore?.currentUser?.id,
    );
  }

  private loadFor(id: number | null) {
    this.dismissedFor = id;
    this.answeredFor = null;
    this.dismissed = id === null ? false : readDismissed(id);
  }

  /** The bell's rows, read where ApiNotificationStore keeps them. */
  private get bell(): BellNotification[] {
    const store = (this.rootStore as unknown as {
      apiNotificationStore?: { notifications?: BellNotification[] };
    }).apiNotificationStore;
    return store?.notifications ?? [];
  }

  /**
   * Whether push already carries a ticket kind — true/false only when the
   * settings store has actually loaded this person's switches, null otherwise.
   */
  get pushAlreadyOn(): boolean | null {
    const settings = (this.rootStore as unknown as {
      notificationSettingsStore?: {
        loaded?: boolean;
        legs?: { push?: string[] };
      };
    }).notificationSettingsStore;
    if (!settings?.loaded) return null;
    return (settings.legs?.push ?? []).some((k) => k.startsWith("ticket"));
  }

  /** Server: are VAPID keys set? null until the settings store has loaded. */
  get pushConfigured(): boolean | null {
    const settings = (this.rootStore as unknown as {
      notificationSettingsStore?: { loaded?: boolean; pushConfigured?: boolean | null };
    }).notificationSettingsStore;
    if (!settings?.loaded) return null;
    return typeof settings.pushConfigured === "boolean" ? settings.pushConfigured : null;
  }

  /** A ticket notification addressed to this person is in the bell. */
  get hasTicketNotification(): boolean {
    return this.bell.some((n) => n.userId != null && isTicketNotification(n));
  }

  get visible(): boolean {
    if (this.dismissedFor === null || this.dismissed) return false;
    if (this.pushAlreadyOn === true) return false;
    // An invitation to something this deployment cannot do is a dead end.
    if (this.pushConfigured === false) return false;
    return this.hasTicketNotification;
  }

  dismiss() {
    const id = this.currentUserId();
    this.dismissed = true;
    if (id !== null) writeDismissed(id);
  }

  /** Go to Settings → Notifications, and count that as the answer. */
  openSettings() {
    const id = this.currentUserId();
    this.dismissed = true;
    if (id !== null) {
      if (this.pushConfigured === true) writeDismissed(id);
      else this.answeredFor = id;
    }
    (this.rootStore as unknown as {
      routerStore?: { goTo?: (name: string, params?: Record<string, unknown>) => unknown };
    }).routerStore?.goTo?.("Settings", { page: "notifications" });
  }

  /**
   * Called by RootStore.purgeUserData on sign-out: forget whose notice this
   * was. The person-change reaction reads the next person's own dismissal.
   */
  reset() {
    this.loadFor(null);
  }
}

export default PushNoticeStore;
